import { Box, Flex, Skeleton, Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'

import { useReportingI18n } from '../i18n'
import BigCounter from './BigCounter'
import ChartWrapper from './ChartWrapper'
import ViewToggle from './ViewToggle'
import type { ViewType } from './ViewToggle'

export interface CounterDefinition {
  value: number
  label: string
  subtitle: string
  isPercentage?: boolean
}

interface ReportingDashboardShellProps {
  timeZone?: string
  dateContextReady: boolean
  isDateRangeTooLarge: boolean
  isLoading: boolean
  hasError: boolean
  hasData: boolean
  viewType: ViewType
  onViewTypeChange: (viewType: ViewType) => void
  counters: CounterDefinition[]
  chartContent: ReactNode
  tableContent: ReactNode
  i18nKeys?: {
    invalidDateRange?: string
    gettingPbx?: string
    dateRangeTooLarge?: string
    dateRangeAdjustPrompt?: string
    errorText?: string
    noDataText?: string
  }
  chartHeaderContent?: ReactNode
  afterContent?: ReactNode
  viewToggleMarginBottom?: string
}

const FALLBACK_KEYS = {
  invalidDateRange: 'Invalid Date Range',
  gettingPbx: 'Getting PBX...',
  dateRangeTooLarge:
    'Date range is too large. Please select a range of 365 days or less.',
  dateRangeAdjustPrompt: 'Please adjust your date range and try again.',
  errorText: 'Error loading data',
  noDataText: 'No data available',
}

export const ReportingDashboardShell = ({
  timeZone,
  dateContextReady,
  isDateRangeTooLarge,
  isLoading,
  hasError,
  hasData,
  viewType,
  onViewTypeChange,
  counters,
  chartContent,
  tableContent,
  i18nKeys = {},
  chartHeaderContent,
  afterContent,
  viewToggleMarginBottom = '1rem',
}: ReportingDashboardShellProps) => {
  const { text } = useReportingI18n()

  if (!dateContextReady) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        minHeight="300px"
      >
        <Text color="fg.muted">
          {timeZone
            ? text(
                i18nKeys.invalidDateRange ?? 'dashboard.invalidDateRange',
                FALLBACK_KEYS.invalidDateRange,
              )
            : text(
                i18nKeys.gettingPbx ?? 'dashboards.gettingPbx',
                FALLBACK_KEYS.gettingPbx,
              )}
        </Text>
      </Flex>
    )
  }

  if (isDateRangeTooLarge) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        minHeight="300px"
        gap="16px"
        padding="32px"
      >
        <Text color="fg.error" textAlign="center" fontWeight="500">
          {text(
            i18nKeys.dateRangeTooLarge ?? 'dashboard.dateRangeTooLarge',
            FALLBACK_KEYS.dateRangeTooLarge,
          )}
        </Text>
        <Text color="fg.muted" fontSize="sm" textAlign="center">
          {text(
            i18nKeys.dateRangeAdjustPrompt ?? 'dashboard.dateRangeAdjustPrompt',
            FALLBACK_KEYS.dateRangeAdjustPrompt,
          )}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex direction="column" w="100%" h="100%" gap="1.25rem" paddingTop="1rem">
      <Flex gap="1rem" w="100%" flexWrap="wrap">
        {isLoading
          ? counters.map((_, i) => (
              <Skeleton
                key={i}
                h="90px"
                flex="1"
                minW="140px"
                borderRadius="md"
              />
            ))
          : counters.map((counter, i) => (
              <BigCounter
                key={i}
                flex="1"
                minW="140px"
                value={counter.value}
                label={counter.label}
                subtitle={counter.subtitle}
                isPercentage={counter.isPercentage}
              />
            ))}
      </Flex>

      <ChartWrapper paddingTop="1.5rem">
        <Flex justifyContent="center" mb={viewToggleMarginBottom}>
          <ViewToggle value={viewType} onChange={onViewTypeChange} />
        </Flex>

        {isLoading ? (
          <Skeleton h="350px" w="100%" borderRadius="md" />
        ) : hasError ? (
          <Text>
            {text(
              i18nKeys.errorText ?? 'dashboard.errorText',
              FALLBACK_KEYS.errorText,
            )}
          </Text>
        ) : !hasData ? (
          <Text>
            {text(
              i18nKeys.noDataText ?? 'dashboard.noDataText',
              FALLBACK_KEYS.noDataText,
            )}
          </Text>
        ) : (
          <>
            {chartHeaderContent}
            {viewType === 'chart' ? chartContent : tableContent}
            {afterContent}
          </>
        )}
      </ChartWrapper>

      <Box height="1px" flexShrink={0} />
    </Flex>
  )
}

export default ReportingDashboardShell
